import documentEventsApi from "@entrilia/shared/api/clients/documentEventsApi";
import { isFileDownloadAllowed } from "@entrilia/shared/utilities/fileHelper";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl, downloadFileFromUrlViaAxios } from "@entrilia/shared/services/downloadFile";
import { api } from "../api/client";
import type { Message } from "../store/state/messages/types";
import { getAuthMode } from "../variables";

const sendDownloadEvent = async (documentViewSessionId: string) => {
  try {
    await documentEventsApi.ingestDocumentDownloadEvent({
      viewEventsData: [
        {
          sessionId: documentViewSessionId,
          eventData: { download: true },
        },
      ],
    });
  } catch (error) {
    logError(error, "[sendDownloadEvent]");
  }
};

export const downloadAttachment = async (messageId: string, attachmentId: string, forInvestorName?: string) => {
  const response = await api.attachments.getUrl(messageId, attachmentId, forInvestorName);
  if (!response.success) {
    logError(response.error, "[downloadAttachment]");
    return;
  }

  const { downloadUrl, documentViewSessionId } = response.data;
  await downloadFileFromUrl(downloadUrl);

  if (documentViewSessionId) {
    sendDownloadEvent(documentViewSessionId);
  }
};

export const downloadAll = async (messageIds: string[], forInvestorName?: string) => {
  const url = await api.attachments.getUrlForAll(messageIds, forInvestorName);
  if (!url) {
    return;
  }

  if (getAuthMode() === "impersonation") {
    downloadFileFromUrlViaAxios(url);
  } else {
    await downloadFileFromUrl(url);
  }
};

export const generateAttachmentUrlData = async (messageId: string, attachmentId: string, forInvestorName?: string) => {
  const response = await api.attachments.getUrl(messageId, attachmentId, forInvestorName);
  if (response?.success === true) {
    return response.data;
  }

  throw new Error(response?.error?.message ?? "Failed to generate attachment URL data");
};

const downloadSingleAttachmentCore = async (messageId: string, forInvestorName?: string) => {
  const messageDetailResponse = await api.messages.getDetails(messageId);
  const attachment = messageDetailResponse?.data?.attachments?.[0];
  if (attachment) {
    // View-only attachments must never be downloaded. This single-attachment path bypasses the
    // backend's bulk-download token (which already excludes view-only files), so guard it here too.
    if (isFileDownloadAllowed(attachment.accessOptions)) {
      await downloadAttachment(messageId, attachment.id, forInvestorName);
    }
    return true;
  }

  return false;
};

export const downloadAllAttachments = async (message: Message, forInvestorName?: string) => {
  if (message.numberOfAttachments === 0) {
    return;
  }

  if (message.numberOfAttachments === 1) {
    if (await downloadSingleAttachmentCore(message.id, forInvestorName)) {
      return;
    }
  }

  await downloadAll([message.id], forInvestorName);
};
